import type { BadgeTone, FleetCar } from '@fleet/shared';
import { apkInfo } from '@fleet/shared';
import { Badge } from '../../design-system';
import { PersoonLink } from '../../components/ui/EntiteitLink';

const COLS = '120px 1.4fr 1.2fr 150px 110px';

/**
 * Het type als badge in dezelfde vorm als de APK-badge ernaast; alleen de kleur
 * verschilt. Eigen beheer stond er eerst als grijze platte tekst tussen twee
 * gekleurde pillen, waardoor het leek of die auto's geen type hadden.
 *
 * Groen voor vast, om dezelfde reden als APK "OK": er is niets aan de hand. Leen
 * krijgt terracotta en lease blauw, de kleuren die de vorige pillen ook hadden.
 *
 * Eigen labels en niet `EIGENDOM_LABEL` uit shared, dat "Eigen bezit" en
 * "Leenauto" zegt. Die woorden staan ook in de mail naar de boekhouding, en één
 * woord per badge houdt deze kolom smal; daar veranderen zou dat bericht
 * meeveranderen.
 */
const TYPE_LABEL: Record<string, string> = { EIGEN: 'Vast', LEEN: 'Leen', LEASE: 'Lease' };
const TYPE_TOON: Record<string, BadgeTone> = {
  EIGEN: 'success',
  LEEN: 'danger',
  LEASE: 'blue',
};

export function FleetTable({
  cars,
  apkReminderDays,
  onSelect,
}: {
  cars: FleetCar[];
  apkReminderDays: number;
  onSelect: (kenteken: string) => void;
}) {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E3D9CC',
        borderRadius: 4,
        overflowX: 'auto',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          gap: 10,
          padding: '12px 20px',
          borderBottom: '2px solid #111111',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#666666',
          minWidth: 640,
        }}
      >
        <span>Kenteken</span>
        <span>Merk / model</span>
        <span>Bestuurder</span>
        <span>Type</span>
        <span>APK</span>
      </div>
      {cars.map((c) => {
        const a = apkInfo(c.apkVervaldatum, apkReminderDays);
        const rowBg =
          c.status === 'GARAGE'
            ? '#FDF6E7'
            : a.dagen != null && a.dagen < 0
              ? '#FBEEEA'
              : '#FFFFFF';
        return (
          <div
            key={c.kenteken}
            onClick={() => onSelect(c.kenteken)}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 10,
              padding: '12px 20px',
              borderBottom: '1px solid #F0E8DC',
              fontSize: 13,
              alignItems: 'center',
              cursor: 'pointer',
              minWidth: 640,
              background: rowBg,
            }}
          >
            <span style={{ fontWeight: 700, fontFamily: "'Roboto Mono', monospace" }}>
              {c.kenteken}
            </span>
            <span style={{ color: '#444444' }}>
              {c.merk} {c.model}
            </span>
            <span>
              {c.bestuurderNaam ? (
                // De rij klikt naar de auto; de naam gaat naar de persoon. De
                // link houdt de rijklik zelf tegen.
                <PersoonLink persoonId={c.persoonId} naam={c.bestuurderNaam} />
              ) : (
                <Badge tone="danger">Niet toegewezen</Badge>
              )}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <Badge tone={TYPE_TOON[c.eigendom] ?? 'neutral'}>
                {TYPE_LABEL[c.eigendom] ?? c.eigendom}
              </Badge>
              {c.status === 'GARAGE' && <Badge tone="warning">Garage</Badge>}
            </span>
            <Badge tone={a.tone}>{a.badge}</Badge>
          </div>
        );
      })}
    </div>
  );
}
